import React, { useEffect, useState } from 'react';
import {
  Button,
  TextField,
  Autocomplete,
  Switch,
  FormControlLabel,
  Stack,
  CircularProgress,
} from '@mui/material';
import FunctionsData from '../../../../Functions/FunctionsData';
import FunctionsConfig from '../../../../Functions/FunctionsConfig'; // Ensure correct path
import moment from 'moment'; // Import moment for date handling

// Reusing ShowPrice component
const ShowPrice = ({ row, customError, dateSurgeryValid, a_surgery_amount }) => {
  return (
    <div style={{ margin: 10 }}>
      Current cost: NZD {a_surgery_amount}.&nbsp;
    </div>
  );
};

// Optional ShowText component if needed
function ShowText({ col, label, row, updateValue, allow, valid, setValid, disabled }) {
  useEffect(() => {
    if (!valid.find((v) => v.slug === col)) {
      setValid([
        ...valid,
        { slug: col, value: row[col] !== '' || (allow && !allow.includes(row[col])) },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [col, row, allow]);

  return (
    <div style={{ marginBottom: !allow ? 20 : null }}>
      <TextField
        error={row[col] === '' || (allow && !allow.includes(row[col]))}
        value={row[col]}
        disabled={disabled}
        onChange={(e) => {
          updateValue(col, e.target.value);
          let valCopy = valid.filter((v) => v.slug !== col);
          setValid([
            ...valCopy,
            {
              slug: col,
              value: e.target.value !== '' && (!allow || allow.includes(e.target.value)),
            },
          ]);
        }}
        placeholder={label}
        fullWidth
      />
      {allow && (
        <div style={{ fontSize: '0.7em', marginBottom: 20 }}>
          Allowed values: {allow.join(', ')}
        </div>
      )}
    </div>
  );
}

export default function LandingCommunityLogsUpdateRedeemed({
  row,
  setLoadingVouchers,
  mode,
  setOpen,
  customError,
  dateSurgeryValid: initialDateSurgeryValid,
  a_surgery_amount: initiala_surgery_amount,
}) {
  const [state, setState] = useState({ ...row });
  const [valid, setValid] = useState([]);

  const [coloursPrimary, setColoursPrimary] = useState([]);
  const [coloursSecondary, setColoursSecondary] = useState([]);
  const [animalTypes, setAnimalTypes] = useState([]);
  const [weights, setWeights] = useState([]);
  const [sources, setSources] = useState([]);
  const [community, setCommunity] = useState(false); // Set this based on your app logic

  const [dateSurgery, setDateSurgery] = useState(
    row.a_date_surgery
      ? moment(row.a_date_surgery).format('YYYY-MM-DD')
      : moment().format('YYYY-MM-DD')
  );
  const [dateSurgeryValid, setDateSurgeryValid] = useState(initialDateSurgeryValid || false);
  const [a_surgery_amount, seta_surgery_amount] = useState(initiala_surgery_amount || 0.0);

  // New state for surgery options
  const [surgeryOptions, setSurgeryOptions] = useState([]);
  const [loadingSurgeryOptions, setLoadingSurgeryOptions] = useState(false);
  const [surgeryOptionsError, setSurgeryOptionsError] = useState(null);

  useEffect(() => {
    if (row) setState({ ...row });
    setCommunity(
      (
          row.campaign_target &&
          (row.campaign_target.includes('community') || row.campaign_target.includes('inspectors'))
      ))
  }, [row]);

  // Fetching config data with mounted check
  useEffect(() => {
    let isMounted = true;

    FunctionsConfig.getPrimaryColours(state.a_type).then((r) => {
      if (isMounted) setColoursPrimary(r);
    });

    FunctionsConfig.getSecondaryColours(state.a_type).then((r) => {
      if (isMounted) setColoursSecondary(r);
    });

    const animalTypesPromise = community
      ? FunctionsConfig.getAnimalTypesArray('species-community')
      : FunctionsConfig.getAnimalTypesArray('species-community');

    animalTypesPromise.then((r) => {
      if (isMounted) setAnimalTypes(r);
    });

    FunctionsConfig.getWeights().then((r) => {
      if (isMounted) setWeights(r);
    });

    FunctionsConfig.getReasonsSource(state.a_type).then((r) => {
      if (isMounted) setSources(r);
    });

    return () => {
      isMounted = false;
    };
  }, [state.a_type, community]);

  // Date Validation
  useEffect(() => {
    if (mode === 'issued') {
      setDateSurgeryValid(true); // No validation needed
      return;
    }

    let isValid = true;
    if (mode !== 'issued') {
      isValid =
       moment(row.created_at).isSameOrBefore(moment(dateSurgery), 'day') &&  // only compare by day
    moment(dateSurgery).isSameOrBefore(moment(), 'day') &&                // surgery date ≤ today
    moment(dateSurgery).isValid();                                        // must be a valid date
    }
    setDateSurgeryValid(isValid);
  }, [dateSurgery, row.created_at, mode]);

  // Calculate Amounts
  useEffect(() => {
    let isMounted = true;
    let a = 0;
    FunctionsConfig.pricingMatrix(
      state.a_type,
      state.a_gender,
      state.a_weight,
      state.a_type_surgery,
      community
    ).then((arr) => {
      if (isMounted && arr) {
        a = parseFloat(arr.price);
        if (state.a_is_pregnant > 0) {
          a += arr.pregnant_add;
        }
        if (isMounted) seta_surgery_amount(a || 0);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [
    state.a_type,
    state.a_gender,
    state.a_weight,
    state.a_type_surgery,
    state.a_is_pregnant,
    state.sponsor_applicant_cost,
    state.sponsor_microchip_extra_cost,
    community,
  ]);

  // Fetch Surgery Options based on gender and type
  useEffect(() => {
    let isMounted = true;

    const fetchSurgeryOptions = async () => {
      setLoadingSurgeryOptions(true);
      setSurgeryOptionsError(null);
      try {
        const gender = state.a_gender;
        const type = state.a_type;
        const options = await FunctionsConfig.getTypeOfSurgeryByType(gender, type);
        if (isMounted) {
          setSurgeryOptions(options);
          // Reset a_type_surgery if the current selection is not in the new options
          if (!options.includes(state.a_type_surgery)) {
            setState((prevState) => ({ ...prevState, a_type_surgery: '' }));
          }
        }
      } catch (error) {
        if (isMounted) {
          setSurgeryOptionsError('Failed to load surgery options.');
          console.error('Error fetching surgery options:', error);
        }
      } finally {
        if (isMounted) setLoadingSurgeryOptions(false);
      }
    };

    if (state.a_gender && state.a_type) {
      fetchSurgeryOptions();
    } else {
      setSurgeryOptions([]);
      setState((prevState) => ({ ...prevState, a_type_surgery: '' }));
    }

    return () => {
      isMounted = false;
    };
  }, [state.a_gender, state.a_type, state.a_type_surgery]);

  function updateValue(col, val) {
    setState({ ...state, [col]: val });
  }

  function submitData() {
    const payload = {
      owner_first: state.owner_first,
      owner_last: state.owner_last,
      owner_email: state.owner_email,
      owner_phone: state.owner_phone,
      a_name: state.a_name,
      a_type: state.a_type,
      a_source: state.a_source,
      a_gender: state.a_gender,
      a_age: state.a_age,
      a_color_primary: state.a_color_primary,
      a_color_secondary: state.a_color_secondary,
      a_weight: state.a_weight,
      a_microchipped: state.a_microchipped,
      a_is_pregnant: state.a_is_pregnant,
      a_was_pregnant: state.a_was_pregnant,
      a_microchip_only_flag: state.a_microchip_only_flag,
      // Conditionally include a_type_surgery based on mode
      ...(mode !== 'issued' && { a_type_surgery: state.a_type_surgery || null }),
    };

    // Conditionally include a_date_surgery and a_surgery_amount based on mode
    if (mode !== 'issued') {
      payload.a_date_surgery = dateSurgeryValid ? dateSurgery : null;
      payload.a_surgery_amount = a_surgery_amount; // Correct reference
    }

    FunctionsData.postData(FunctionsData.getApiUrl('/api/voucher/update/') + row.id, payload)
      .then(() => {
        setLoadingVouchers(true);
        setOpen(false);
      })
      .catch((e) => {
        console.log(e);
      });
  }

  // Determine if price should be shown
  const hasProcedure =
    state.a_type_surgery &&
    state.a_type_surgery.trim() !== '' &&
    state.a_type_surgery !== 'null';
  const canShowPrice =
    mode === 'issued' ? hasProcedure : hasProcedure && dateSurgeryValid;

  return (
    <div style={{ width: '100%' }}>
      <h1 style={{ marginBottom: 20 }}>{row?.sn}</h1>

      {/* Owner/Animal Details */}
      <TextField
        label={community ? 'Animal name (Community)' : 'Animal name'}
        value={state.a_name || ''}
        onChange={(e) => updateValue('a_name', e.target.value)}
        fullWidth
        error={!state.a_name || state.a_name.trim() === ''}
        style={{ marginBottom: 20 }}
      />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1, sm: 2, md: 4 }}
        sx={{ marginBottom: 2 }}
      >
        <Autocomplete
          disableClearable
          value={state.a_type || ''}
          options={animalTypes}
          onChange={(e, v) => updateValue('a_type', v)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Type"
              error={!state.a_type || state.a_type.trim() === ''}
            />
          )}
          sx={{ width: '100%' }}
        />

        <Autocomplete
          disableClearable
          value={state.a_source || ''}
          options={sources}
          onChange={(e, v) => updateValue('a_source', v)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Source"
              error={!state.a_source || state.a_source.trim() === ''}
            />
          )}
          sx={{ width: '100%' }}
        />
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1, sm: 2, md: 4 }}
        sx={{ marginBottom: 2 }}
      >
        <Autocomplete
          disableClearable
          value={state.a_gender || ''}
          options={FunctionsConfig.getGenders()}
          onChange={(e, v) => updateValue('a_gender', v)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Sex"
              error={!state.a_gender || state.a_gender.trim() === ''}
            />
          )}
          sx={{ width: '100%' }}
        />

        <Autocomplete
          disableClearable
          value={state.a_age || ''}
          options={FunctionsConfig.getAges()}
          onChange={(e, v) => updateValue('a_age', v)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Age"
              error={!state.a_age || state.a_age.trim() === ''}
            />
          )}
          sx={{ width: '100%' }}
        />
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1, sm: 2, md: 4 }}
        sx={{ marginBottom: 2 }}
      >
        <Autocomplete
          disableClearable
          value={state.a_color_primary || ''}
          options={coloursPrimary}
          onChange={(e, v) => updateValue('a_color_primary', v)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Primary Color"
              error={!state.a_color_primary || state.a_color_primary.trim() === ''}
            />
          )}
          sx={{ width: '100%' }}
        />

        <Autocomplete
          disableClearable
          value={state.a_color_secondary || ''}
          options={coloursSecondary}
          onChange={(e, v) => updateValue('a_color_secondary', v)}
          renderInput={(params) => <TextField {...params} label="Secondary Color" />}
          sx={{ width: '100%' }}
        />
      </Stack>

      {state.a_type === 'Dog' && (
        <Autocomplete
          disableClearable
          value={state.a_weight || ''}
          options={weights}
          onChange={(e, v) => updateValue('a_weight', v)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Weight"
              error={!state.a_weight || state.a_weight.trim() === ''}
            />
          )}
          sx={{ width: '100%', marginBottom: 2 }}
        />
      )}

      {/* Conditionally render the Procedure Autocomplete based on mode */}
      {mode !== 'issued' && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1, sm: 2, md: 4 }}
          sx={{ marginBottom: 2 }}
        >
          <Autocomplete
            disableClearable
            id="a_type_surgery_form_field"
            size="full"
            value={state.a_type_surgery || ''}
            options={surgeryOptions}
            loading={loadingSurgeryOptions}
            disabled={loadingSurgeryOptions || !!surgeryOptionsError}
            sx={{ width: '100%' }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Procedure"
                error={
                  mode !== 'issued' &&
                  (
                    !state.a_type_surgery ||
                    state.a_type_surgery === 'null' ||
                    (
                      (state.a_gender === 'Female' &&
                        !['Spay', 'Pregnant spay', 'Microchip only'].includes(state.a_type_surgery)) ||
                      (state.a_gender === 'Male' &&
                        !['Neuter', 'Cryptorchid', 'Microchip only'].includes(state.a_type_surgery))
                    )
                  )
                }
                helperText={surgeryOptionsError}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingSurgeryOptions ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            onChange={(e, v) => updateValue('a_type_surgery', v)}
          />
        </Stack>
      )}

      {/* Conditionally render Date of Procedure field if mode is not 'issued' */}
      {mode !== 'issued' && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1, sm: 2, md: 4 }}
          sx={{ marginBottom: 2 }}
        >
          <TextField
            error={!dateSurgeryValid}
            sx={{ width: '100%' }}
            label="Date of procedure"
            type="date"
            value={dateSurgery}
            focused
            onChange={(e) => {
              const newDate = moment(e.target.value).isValid()
                ? moment(e.target.value).format('YYYY-MM-DD')
                : '';
              setDateSurgery(newDate);
            }}
            onBlur={(e) => {
              const newDate = moment(e.target.value).isValid()
                ? moment(e.target.value).format('YYYY-MM-DD')
                : null;
              updateValue('a_date_surgery', newDate);
            }}
            InputProps={{
              inputProps: {
                min: moment(row.created_at).format('YYYY-MM-DD'),
                max: moment().format('YYYY-MM-DD'),
                onKeyDown: (e) => {
                  if (e?.preventDefault) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                },
              },
            }}
            style={{ marginBottom: 20 }}
          />

          {canShowPrice && (
            <ShowPrice
              row={state}
              customError={customError}
              dateSurgeryValid={dateSurgeryValid}
              a_surgery_amount={a_surgery_amount}
            />
          )}
        </Stack>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1, sm: 2, md: 4 }}
        sx={{ marginBottom: 2 }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={state.a_microchipped || false}
              onChange={(e) => updateValue('a_microchipped', e.target.checked)}
            />
          }
          label="Microchipped"
        />
        <FormControlLabel
          control={
            <Switch
              checked={state.a_is_pregnant || false}
              onChange={(e) => updateValue('a_is_pregnant', e.target.checked)}
            />
          }
          label="Pregnant"
        />
      </Stack>

      <FormControlLabel
        control={
          <Switch
            checked={state.a_was_pregnant || false}
            onChange={(e) => updateValue('a_was_pregnant', e.target.checked)}
          />
        }
        label="Recently Pregnant"
      />

      <FormControlLabel
        control={
          <Switch
            checked={state.a_microchip_only_flag || false}
            onChange={(e) => updateValue('a_microchip_only_flag', e.target.checked)}
          />
        }
        label="Microchip only, no desexing required"
        sx={{ marginBottom: 2 }}
      />

      <div>
        <Button
          disabled={
            valid.filter((v) => v.value === false).length > 0 ||
            (mode !== 'issued' && !dateSurgeryValid)
          }
          variant="contained"
          onClick={submitData}
          style={{ marginRight: 10 }}
        >
          Submit
        </Button>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
}
